import React, { useState, useRef } from "react";
import { Tabs, Tab, Box, Typography } from '@mui/material';

const TabsLayout = ({
  tabs = [],
  children,
  title = "Data Management",
  defaultTab = null,
  onTabChange = null,
  className = "",
  actionButtons = null
}) => {
  const [activeTab, setActiveTab] = useState(defaultTab || tabs[0]?.id || tabs[0]);
  const tabsRef = useRef(null);

  const scrollTabs = (direction) => {
    if (tabsRef.current) {
      const scrollAmount = 100;
      tabsRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    if (onTabChange) {
      onTabChange(tabId);
    }
  };

  const getActiveTabTitle = () => {
    const tab = tabs.find(tab => {
      const tabId = typeof tab === 'string' ? tab : tab.id;
      return tabId === activeTab;
    });

    if (typeof tab === 'string') {
      return tab;
    }

    return tab?.title || tab?.label || activeTab;
  };

  const getActiveTabDescription = () => {
    const tab = tabs.find(tab => {
      const tabId = typeof tab === 'string' ? tab : tab.id;
      return tabId === activeTab;
    });

    return tab?.description || '';
  };

  const renderContent = () => {
    if (typeof children === 'function') {
      return children(activeTab);
    }

    if (React.Children.count(children) > 0) {
      return React.Children.toArray(children).find(child =>
        child.props?.tabId === activeTab
      ) || children;
    }

    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-4">
        <div className="w-32 h-32 mb-4 opacity-60 bg-gray-200 rounded-lg flex items-center justify-center">
          <span className="text-gray-400 text-4xl">📄</span>
        </div>
        <span className="text-lg font-semibold text-gray-800 mb-1">No Data Available</span>
        <span className="text-sm text-gray-500 w-[300px]">
          Content for <strong>{activeTab}</strong> will be implemented soon.
        </span>
      </div>
    );
  };

  const getTabIndex = () => {
    return tabs.findIndex(tab => {
      const tabId = typeof tab === 'string' ? tab : tab.id;
      return tabId === activeTab;
    });
  };

  const handleMUITabChange = (event, newValue) => {
    const selectedTab = tabs[newValue];
    const tabId = typeof selectedTab === 'string' ? selectedTab : selectedTab.id;
    handleTabChange(tabId);
  };

  return (
    <div className={`h-full flex flex-col bg-gray-50 ${className}`}>
      {/* Material-UI Tabs */}
      <Box sx={{
        borderBottom: '1px solid #e0e0e0',
        backgroundColor: 'white',
        px: 2
      }}>
        <Tabs
          value={getTabIndex()}
          onChange={handleMUITabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontFamily: 'Poppins',
              fontSize: '14px',
              fontWeight: 500,
              color: '#6B7280',
              minHeight: 48,
              px: 2,
              py: 1.5,
              letterSpacing: '0.2px',
              '&.Mui-selected': {
                color: '#111827',
                fontWeight: 600,
                fontFamily: 'Poppins'
              },
              '&:hover': {
                color: '#374151'
              }
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#6B7280',
              height: 2
            },
            '& .MuiTabs-scroller': {
              '& .MuiTabs-flexContainer': {
                gap: 0
              }
            }
          }}
        >
          {tabs.map((tab, index) => {
            const tabLabel = typeof tab === 'string' ? tab : tab.label;
            return (
              <Tab
                key={index}
                label={tabLabel}
              />
            );
          })}
        </Tabs>
      </Box>

      {/* Content area */}
      <div className="flex-1 bg-white relative overflow-hidden">
        {/* Title and Description Section with Action Buttons */}
        <Box sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 2
        }}>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h6"
              component="h1"
              gutterBottom={false}
              sx={{
                fontSize: '16px',
                fontFamily: 'Poppins',
                fontWeight: 600,
                color: '#000'
              }}
            >
              {getActiveTabTitle()}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontFamily: 'Poppins',
                color: '#9AA6AC',
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: 1.5
              }}
            >
              {getActiveTabDescription()}
            </Typography>
          </Box>
          {actionButtons && (
            <Box sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              flexShrink: 0
            }}>
              {actionButtons}
            </Box>
          )}
        </Box>

        {/* Border below description */}
        <Box sx={{ borderBottom: '1px solid #e0e0e0', mb: 0.5 }} />

        {/* Main Content */}
        <div className="p-0">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default TabsLayout;